import type { AvailabilityBlock } from "@store/disponibiliteStore";

export interface BlockLayout {
  blockId: string;
  colIndex: number;
  colCount: number;
}

/**
 * Computes side-by-side column assignments for overlapping blocks.
 * Returns a map from blockId to { colIndex, colCount }.
 *
 * Algorithm:
 * 1. Sort blocks by startHour asc, then durationHours desc
 * 2. Greedily assign each block to the first column where it doesn't overlap
 * 3. Group connected overlapping blocks and normalize colCount within each group
 */
export function computeOverlapLayout(blocks: AvailabilityBlock[]): Map<string, BlockLayout> {
  if (blocks.length === 0) return new Map();

  const sorted = [...blocks].sort((a, b) => {
    if (a.startHour !== b.startHour) return a.startHour - b.startHour;
    return b.durationHours - a.durationHours;
  });

  const columns: AvailabilityBlock[][] = [];
  const blockCol = new Map<string, number>();

  for (const block of sorted) {
    const end = block.startHour + block.durationHours;
    let placed = false;
    for (let col = 0; col < columns.length; col++) {
      const overlaps = columns[col].some((existing) => {
        const existingEnd = existing.startHour + existing.durationHours;
        return block.startHour < existingEnd && end > existing.startHour;
      });
      if (!overlaps) {
        columns[col].push(block);
        blockCol.set(block.id, col);
        placed = true;
        break;
      }
    }
    if (!placed) {
      columns.push([block]);
      blockCol.set(block.id, columns.length - 1);
    }
  }

  const visited = new Set<string>();
  const result = new Map<string, BlockLayout>();

  function blocksOverlap(a: AvailabilityBlock, b: AvailabilityBlock): boolean {
    return a.startHour < b.startHour + b.durationHours && b.startHour < a.startHour + a.durationHours;
  }

  function findGroup(start: AvailabilityBlock): AvailabilityBlock[] {
    const group: AvailabilityBlock[] = [];
    const stack = [start];
    while (stack.length > 0) {
      const current = stack.pop()!;
      if (visited.has(current.id)) continue;
      visited.add(current.id);
      group.push(current);
      for (const other of sorted) {
        if (!visited.has(other.id) && blocksOverlap(current, other)) {
          stack.push(other);
        }
      }
    }
    return group;
  }

  for (const block of sorted) {
    if (visited.has(block.id)) continue;
    const group = findGroup(block);
    let maxCol = 0;
    for (const b of group) {
      const col = blockCol.get(b.id) ?? 0;
      if (col > maxCol) maxCol = col;
    }
    const colCount = maxCol + 1;
    for (const b of group) {
      result.set(b.id, {
        blockId: b.id,
        colIndex: blockCol.get(b.id) ?? 0,
        colCount,
      });
    }
  }

  return result;
}
